import { useCallback, useEffect, useState } from 'react';
import { api, type Company, type PitAnnual } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { money } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

/**
 * Rozliczenie roczne osoby fizycznej (E9-S1).
 *
 * Stoi przy remanencie, bo to z niego bierze się dochód roczny: towar, który został na półce,
 * nie jest kosztem tego roku. Kwota „do dopłaty" jest różnicą między podatkiem za rok a sumą
 * zaliczek policzonych po drodze — i to ona zwykle zaskakuje, więc stoi na wierzchu.
 *
 * Pod spodem mówimy, **czego ten rachunek nie obejmuje**: innych źródeł dochodu, ulg i wspólnego
 * rozliczenia z małżonkiem. Bez tego ktoś wziąłby tę liczbę za kwotę z zeznania, a to podatek
 * od samej działalności.
 */
export function PitAnnualPanel({
  company,
  year,
  t,
  locale,
  onGo,
}: {
  company: Company;
  year: number;
  t: Dictionary;
  locale: Locale;
  onGo?: (panel: Panel) => void;
}) {
  const [rows, setRows] = useState<PitAnnual[] | null>(null);
  const [loss, setLoss] = useState('');
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api.pitAnnual(company.id, year, loss.trim() || undefined));
    } catch (failure) {
      setRows(null);
      setError(failureOf(failure));
    }
  }, [company.id, year, loss]);

  useEffect(() => {
    void load();
  }, [load]);

  if (rows !== null && rows.length === 0) return null;

  return (
    <div className="panel">
      <header>
        <h4>
          {t.pitAnnual.title} {year}
        </h4>
        <div className="sp">
          <label className="sm muted" htmlFor="pitLoss">
            {t.pitAnnual.priorYearLoss}
          </label>
          <input
            id="pitLoss"
            className="inp n sm"
            inputMode="decimal"
            value={loss}
            onChange={(event) => setLoss(event.target.value)}
          />
        </div>
      </header>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {rows?.map((row) => (
        <div key={row.partnerId ?? 'own'} className="panel">
          <header>
            <h5>
              {t.pitAnnual.forms[row.form]}
              {row.taxpayer && ` · ${row.taxpayer}`}
            </h5>
            {/* Dodatnia różnica to dopłata, ujemna — nadpłata; to ona zwykle zaskakuje */}
            <div className="sp">
              <span className={Number(row.difference) > 0 ? 'chip st-warn' : 'chip st-ok'}>
                <i />
                {Number(row.difference) >= 0 ? t.pitAnnual.toPay : t.pitAnnual.toRefund}{' '}
                {money(Math.abs(Number(row.difference)), locale)}
              </span>
            </div>
          </header>
          <dl className="brk">
            {row.form === 'PIT_28' ? (
              <>
                <dt>{t.pitAnnual.revenue}</dt>
                <dd>{money(row.revenue, locale)}</dd>
              </>
            ) : (
              <>
                <dt>{t.pitAnnual.income}</dt>
                <dd>{money(row.income, locale)}</dd>
              </>
            )}
            {/* Korekta złych długów nie ma jej w księdze, więc bez tej pozycji dochód by się nie zgadzał */}
            {Number(row.badDebtAdjustment) !== 0 && (
              <>
                <dt>{t.pitAnnual.badDebt}</dt>
                <dd>{money(row.badDebtAdjustment, locale)}</dd>
              </>
            )}
            <dt>{t.pitAnnual.social}</dt>
            <dd>{money(row.socialPaid, locale)}</dd>
            <dt>{t.pitAnnual.health}</dt>
            <dd>{money(row.healthPaid, locale)}</dd>
            <dt>{t.pitAnnual.deducted}</dt>
            <dd>{money(row.deducted, locale)}</dd>
            {Number(row.priorYearLoss) > 0 && (
              <>
                <dt>{t.pitAnnual.priorYearLoss}</dt>
                <dd>{money(row.priorYearLoss, locale)}</dd>
              </>
            )}
            <dt>{t.pitAnnual.taxBase}</dt>
            <dd>{money(row.taxBase, locale)}</dd>
            <dt>{t.pitAnnual.tax}</dt>
            <dd>{money(row.tax, locale)}</dd>
            <dt>{t.pitAnnual.advances}</dt>
            <dd>{money(row.advancesPaid, locale)}</dd>
            <dt className="tot">{Number(row.difference) >= 0 ? t.pitAnnual.toPay : t.pitAnnual.toRefund}</dt>
            <dd className="tot">{money(Math.abs(Number(row.difference)), locale)}</dd>
          </dl>
        </div>
      ))}

      {/* Czego rachunek nie obejmuje — zdaniami z serwera, nie skrótem */}
      {rows?.[0]?.notes.map((note) => (
        <p className="note" key={note}>
          <Icon.Info className="inline-icon" /> {t.pitAnnual.notes[note]}
        </p>
      ))}
    </div>
  );
}
